import { useState, useEffect } from 'react'
import { Layout, Menu, Avatar, Dropdown, Button, Space, Badge } from 'antd'
import {
  UserOutlined, LogoutOutlined, SettingOutlined, PlusOutlined, TeamOutlined, MailOutlined, DatabaseOutlined
} from '@ant-design/icons'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { useUserStore } from '../store/user'
import { fragmentApi } from '../api/fragment'
import TeamList from '../pages/TeamList'
import TeamDetail from '../pages/TeamDetail'
import SensitiveWordManage from '../pages/SensitiveWordManage'
import UserSettings from '../pages/UserSettings'
import MentionList from '../pages/MentionList'
import AdminDashboard from '../pages/AdminDashboard'

const { Header, Sider, Content } = Layout

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const navigate = useNavigate()
  const userInfo = useUserStore((state) => state.userInfo)
  const logout = useUserStore((state) => state.logout)
  const location = useLocation()

  const loadUnreadCount = async () => {
    try {
      const data = await fragmentApi.getMentionList()
      const unread = data.filter(item => !item.isMentionRead).length
      setUnreadCount(unread)
    } catch {
      setUnreadCount(0)
    }
  }

  const menuItems = [
    { key: '/', label: '我的小组', icon: <TeamOutlined /> },
    {
      key: '/mentions',
      label: (
        <Badge count={unreadCount}>
          我的@
        </Badge>
      ),
      icon: <MailOutlined />
    },
    { key: '/sensitive-words', label: '敏感词管理', icon: <SettingOutlined /> },
    { key: '/user-settings', label: '个人中心', icon: <UserOutlined /> },
  ]

  // 如果是管理员，添加管理后台菜单
  if (userInfo?.isAdmin) {
    menuItems.splice(2, 0, {
      key: '/admin',
      label: '管理后台',
      icon: <DatabaseOutlined />
    })
  }

  const userMenuItems = [
    { key: 'settings', label: '个人中心', icon: <UserOutlined /> },
    { key: 'logout', label: '退出登录', icon: <LogoutOutlined /> },
  ]

  const handleMenuClick = (e: { key: string }) => {
    if (e.key === 'logout') {
      logout()
      navigate('/login')
    } else if (e.key === 'settings') {
      navigate('/user-settings')
    }
  }

  useEffect(() => {
    loadUnreadCount()
    const timer = setInterval(loadUnreadCount, 30000)
    return () => clearInterval(timer)
  }, [])

  return (
    <Layout className="min-h-screen">
      <Header className="bg-white border-b border-gray-200 flex items-center justify-between px-6">
        <div className="text-xl font-bold text-blue-600">思想碎片</div>
        <Space>
          <Dropdown menu={{ items: userMenuItems, onClick: handleMenuClick }}>
            <div className="flex items-center cursor-pointer">
              <Avatar src={userInfo?.avatar} icon={<UserOutlined />} />
              <span className="ml-2">{userInfo?.nickname}</span>
            </div>
          </Dropdown>
        </Space>
      </Header>
      <Layout>
        <Sider width={250} theme="light">
          <div className="p-4">
            <Button type="primary" block onClick={() => navigate('/teams/create')} icon={<PlusOutlined />}>
              创建小组
            </Button>
          </div>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
          />
        </Sider>
        <Content className="p-6 bg-gray-50">
          <Routes>
            <Route path="/" element={<TeamList />} />
            <Route path="/team/:id" element={<TeamDetail />} />
            <Route path="/mentions" element={<MentionList />} />
            <Route path="/sensitive-words" element={<SensitiveWordManage />} />
            <Route path="/user-settings" element={<UserSettings />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  )
}
